import React, {useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';

import {Loading, FastGrowingCareer} from '../../elements';
import RootContext from '../../services/context-states/root-context';
import {assessmentApi} from '../../api/openapi-axios-client';
import {callApi} from '../../api/helpers';
import {fastGrowingCareer} from '../../helpers/strings';
import { CareerNavigationData, ONetCareerDetails, VideoHistoryRatingOccupationData } from '../../types';
import { VideoHistoryRating } from '../../types';
import { getClusterImage } from '../../helpers/images';
import { trackBehaviorEvent } from '../../services/behaviorTracking/trackBehaviorEvent';

const ResultsPage: React.FC = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const {tokenContext, userContext} = useContext(RootContext);
    const {token} = tokenContext;    
    const [resultData, setResultData] = useState<VideoHistoryRating[]>([]);
    const [visibleLimit, setVisibleLimit] = useState<number>(3);
    const {currentUser} = userContext;
    
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    useEffect(() => {
        const getData = async (): Promise<void> => {
            try {
                const data = await callApi<VideoHistoryRating[]>(() => assessmentApi.ratings.getUserHistoryRating(currentUser?.id ?? '', true));

                if(data)  {
                    setResultData(data);
                }
    
                setIsLoading(false);
            }
            catch {
                setIsLoading(false);
            }
        };

        if(token && !resultData.length) {
            getData();
        }
    }, [token, resultData.length, currentUser?.id]);

    const BuildClusterObject = (data: VideoHistoryRating): React.ReactElement => {

        return (
            <Box key={`result-${data.category}`} sx={{
                display:'flex',
                flexDirection:'column',
                boxShadow: 4,
                cursor: 'pointer',
                background: 'container.background',
                width: { xs: 'calc(33.333% - 8px)', sm: 275 },
                minWidth: { xs: 'calc(33.333% - 8px)', sm: 275 },
                maxWidth: { xs: 'calc(33.333% - 8px)', sm: 275 },
                padding: 0,
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'transform 0.2s',
                flexShrink: { xs: 1, sm: 0 },
                '&:hover': {
                    transform: 'scale(1.02)',
                }
            }} onClick={() => {
                    trackBehaviorEvent('video-selected-from-results', { categoryId: data.categoryId, categoryCode: data.categoryCode });
                    navigate(`/careers/details/${currentUser?.id}/${data.categoryId}/${Number(data.categoryCode).toFixed(0)}`, {state: {clusterData: data}});
                }}>
                <Box sx={{display:'flex'}}>
                    <img src={getClusterImage(data.categoryCode)} alt={`Cluster illustration for ${data.categoryCode || 'career cluster'}`} style={{overflow: 'hidden', objectFit: 'cover', width: '100%', height: isMobile ? '90px' : '155px'}}/>
                </Box>
                <Box sx={{display: 'flex', flexDirection: 'column', flexGrow: 1, gap: { xs: 0.5, sm: 1 }, padding: { xs: 1, sm: 2 }}}>
                    <Typography sx={{fontWeight: 600, fontSize: { xs: '0.7rem', sm: '10pt' }, marginRight: 2, flexGrow: 1, color: 'text.primary', lineHeight: 1.2}}>{data.category}</Typography>
                    <Button variant="contained" color="primary" size='small' sx={{width: 'fit-content', fontSize: { xs: '0.6rem', sm: '0.875rem' }, py: { xs: 0.3, sm: 1 }, px: { xs: 0.8, sm: 1 }}} aria-label="Learn more about this career cluster">Learn more</Button>
                </Box>
            </Box>
        );
    }

    const buildOccupationItem = (cluster: VideoHistoryRating, occupation: VideoHistoryRatingOccupationData): React.ReactElement | null => {
        if(occupation.averageRating < 4) {
            return null;
        }

        const careerData: CareerNavigationData = {
            backLabel: 'Results',
            categoryId: cluster.categoryId,
            category: cluster.category,
            occupation: occupation.onet ? {
                ...occupation.onet,
                details: occupation.onet
            } as ONetCareerDetails & { details: ONetCareerDetails } : {} as ONetCareerDetails
        };

        return (
            <Box key={occupation.code} sx={{
                cursor:'pointer',
                background: 'container.background',
                border: 'none',
                borderBottom: '1px solid',
                borderColor: 'container.border',
                borderRadius: 0,
                p: { xs: 1.5, sm: 2 },
                mb: 0,
                transition: 'all 0.2s',
                '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: { xs: 'none', sm: 'translateX(4px)' },
                },
                '&:last-child': {
                    borderBottom: 'none'
                }
            }} onClick={() => {
                    trackBehaviorEvent('video-selected-from-results', { occupationId: occupation.occupationId, categoryId: cluster.categoryId });
                    navigate(`/careers/details/${currentUser?.id}/${cluster.categoryId}/career/${cluster.categoryId}-${occupation.occupationId}-${occupation.occupationId}`, {state: {careerData}});
                }}>
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    {(occupation as any)?.onet?.tags?.bright_outlook && (
                        <Box sx={{
                            position: 'absolute',
                            top: { xs: 0, sm: '50%' },
                            right: { xs: 36, sm: 'auto' },
                            transform: { xs: 'none', sm: 'translateY(-50%)' },
                            display: { xs: 'block', sm: 'none' }
                        }}>
                            <Typography sx={{fontSize: 12, color: 'text.fastCareer'}}>
                                {fastGrowingCareer}
                            </Typography>
                        </Box>
                    )}
                    <Grid container columnSpacing={{ xs: 1, sm: 2 }} rowSpacing={{ xs: 0.5, sm: 0 }} sx={{alignItems: { xs: 'flex-start', sm: 'center' }, flex: 1}}>
                        <Grid size={{ xs: 10, sm: 6 }}>
                            <Typography sx={{fontSize: { xs: 14, sm: 14 }, fontWeight: { xs: 600, sm: 400 }, color: 'text.primary', lineHeight: 1.4, pr: { xs: 1, sm: 0 }}}>
                                {occupation.title}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography sx={{fontSize: { xs: 12, sm: 14 }, color: 'text.secondary'}}>
                                {cluster.category}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 0, sm: 2 }} sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'end', alignItems: 'center'}}>
                                {(occupation as any)?.onet?.tags?.bright_outlook && (
                                    <Typography sx={{fontSize: 14, color: 'text.fastCareer', marginRight: 5}}>
                                        {fastGrowingCareer}
                                    </Typography>) }
                                <KeyboardArrowRight sx={{color: 'grey.500', fontSize: '2.0rem', alignSelf: 'end'}}/>
                            </Box>
                        </Grid>
                    </Grid>
                    <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', ml: 1 }}>
                        <KeyboardArrowRight sx={{color: 'grey.500', fontSize: '1.5rem'}}/>
                    </Box>
                </Box>
            </Box>
        );
    }

    const EmptyData = (): React.ReactElement => {
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: 10, 
                alignItems: 'center', justifyContent: 'center', padding: '16px', 
                backgroundColor: theme.palette.container.background,
                marginTop: '5%',
                width: '80%',
                borderRadius: 2,
                border: 1,
                borderColor: theme.palette.container.border}}>
                <PrivacyTipIcon sx={{fontSize: 36, color: 'text.primary'}}/>
                <Typography sx={{fontSize: 16, color: 'text.primary'}}>
                    We're still learning more about your career preferences. Until then, please watch more videos to help us learn more about your career interests.
                </Typography>
            </div>
        )
    }

    const ResultData = (): React.ReactElement => {
        return (
            <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
                <Typography sx={{
                    fontSize: { xs: 16, sm: 20, md: 22 },
                    fontWeight: 900,
                    color: 'text.primary',
                    mb: { xs: 1.5, sm: 2 }
                }}>
                    Top 3 Industry Matches
                </Typography>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: { xs: 1.5, sm: 3, md: 4 },
                    mt: { xs: 1, sm: 3 },
                    mb: 3,
                    overflowX: { xs: 'visible', sm: 'auto' },
                    pb: { xs: 0, sm: 2 },
                    justifyContent: { xs: 'space-between', sm: 'flex-start' }
                }}>
                    {resultData.slice(0, visibleLimit).map((data) => BuildClusterObject(data))}
                </Box>
                <Divider sx={{marginTop: 3, marginBottom: 3}}/>
                <Typography sx={{
                    fontSize: { xs: 18, sm: 20, md: 22 },
                    fontWeight: 900,
                    color: 'text.primary',
                    mb: 1
                }}>
                    Recommended Career Paths Based on Videos Watched
                </Typography>
                <Typography sx={{
                    fontSize: { xs: 13, sm: 14 },
                    marginTop: 1,
                    color: 'text.primary',
                    mb: 2
                }}>
                    These are the career paths we'd recommend based on the videos you've watched. Watch more videos to uncover more career paths.
                </Typography>
                <FastGrowingCareer sx={{marginTop: 1}}/>
                <Box padding={{ xs: 1.5, sm: 2 }} sx={{marginTop:1, borderRadius: 2, border: 1, borderColor: 'container.border', bgcolor: 'container.background'}} aria-label="Your Results Table">
                    <Grid container spacing={2} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                        <Grid size={6}>
                            <Typography sx={{fontSize: 16, color: 'text.secondary'}}>
                                Occupations
                            </Typography>
                        </Grid>
                        <Grid size={4}>
                            <Typography sx={{fontSize: 16, color: 'text.secondary'}}>
                                Industry
                            </Typography>
                        </Grid>
                        <Grid size={2}>
                            <Typography sx={{fontSize: 16, color: 'text.secondary'}}>
                                Occupation Outlook
                            </Typography>
                        </Grid>
                    </Grid>
                    <Divider sx={{marginTop: { xs: 0, sm: 1 }, marginBottom: { xs: 1, sm: 0 }, color: 'divider', background: 'divider', borderColor: 'divider', display: { xs: 'none', sm: 'block' }}}/>
                    {resultData.map((cluster) => cluster.occupationData.map((occupation) => buildOccupationItem(cluster, occupation)))}
                </Box>
            </Container>
        )
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: 'calc(100vh - 100px)', sm: 'calc(100vh - 64px)' }
        }}>
            {
                (isLoading && <Loading />) || ((resultData.length === 0 || resultData.length && !resultData.some((cluster) => cluster.occupationData.some((occupation) => occupation.averageRating > 2.5))) && <EmptyData />) || <ResultData />
            }
        </Box>
    )
}

export default ResultsPage; 